import { SIDE_GROUPS } from 'components/HomepageV3/data/demoData';
import type { FilterSelection } from 'components/HomepageV3/components/FilterSidebar';
import { useSyncExternalStore } from 'react';

/**
 * Build a clean `/explore` route, optionally carrying one extra query key.
 * No `se3` param — editorial Explore is chosen server-side from the flag
 * (see app/explore/page.jsx), so production URLs stay clean.
 */
const SE3_ROUTE_RE = /^\/(?:$|explore(?:\/|$)|private(?:\/|$)|offering(?:\/|$))/;
const STARTENGINE_HOST_RE = /(?:^|\.)startengine\.com$/i;
const subscribeToLocation = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('popstate', onStoreChange);
  return () => window.removeEventListener('popstate', onStoreChange);
};

/** Hydration-safe access to the current query string for SE3 QA links. */
export const useSe3QaSearch = (): string =>
  useSyncExternalStore(
    subscribeToLocation,
    () => window.location.search,
    () => '',
  );

/**
 * Keep explicit editorial QA overrides while navigating between SE3 surfaces.
 * PostHog-bucketed production visitors intentionally keep clean URLs; query
 * parameters are only carried when the current URL explicitly opted into a
 * forced design-review experience.
 */
export const withSe3QaOverride = (href: string, currentSearch?: string): string => {
  const search = currentSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
  const sourceParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const sourceSe3 = sourceParams.get('se3');
  const preserveSe3 =
    sourceParams.has('se3') && (sourceSe3 === '' || sourceSe3 === '1' || sourceSe3 === 'true');
  const sourceTombstoneVariant = sourceParams.get('tombstone_variant')?.trim().toLowerCase();
  const preserveTombstoneVariant =
    sourceTombstoneVariant === 'a' ||
    sourceTombstoneVariant === 'b' ||
    sourceTombstoneVariant === 'c' ||
    sourceTombstoneVariant === 'control' ||
    sourceTombstoneVariant === 'marketplace' ||
    sourceTombstoneVariant === 'marketplace-overlay' ||
    sourceTombstoneVariant === 'hybrid';
  if (!preserveSe3 && !preserveTombstoneVariant) return href;

  const isAbsolute = /^https?:\/\//i.test(href);
  if (!isAbsolute && (!href.startsWith('/') || href.startsWith('//'))) return href;

  try {
    const url = new URL(href, 'https://www.startengine.com');
    if (isAbsolute && !STARTENGINE_HOST_RE.test(url.hostname)) return href;
    if (!SE3_ROUTE_RE.test(url.pathname)) return href;
    if (preserveSe3 && !url.searchParams.has('se3')) url.searchParams.set('se3', '1');
    if (preserveTombstoneVariant && !url.searchParams.has('tombstone_variant')) {
      url.searchParams.set('tombstone_variant', sourceTombstoneVariant);
    }
    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};

const buildSe3ExploreQuery = (
  key?: string,
  value?: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => {
  const params = new URLSearchParams();
  const trimmed = (value ?? '').trim();
  if (key && trimmed) {
    params.set(key, trimmed);
  }
  const qs = params.toString();
  const href = qs ? `/explore?${qs}` : '/explore';
  return withSe3QaOverride(href, currentSearch) as '/explore' | `/explore?${string}`;
};

export const buildSe3ExploreRoute = (
  value?: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => buildSe3ExploreQuery('search', value, currentSearch);

export const buildSe3ExploreSectionRoute = (
  section: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => buildSe3ExploreQuery('section', section, currentSearch);

export const buildSe3ExploreFilterRoute = (
  filter: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => buildSe3ExploreQuery('filter', filter, currentSearch);

export const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * Resolve a `section=` / `filter=` URL slug to the matching sidebar selection so
 * deep links from "View all" shelves and footer filters open the requested
 * shelf/filter instead of the generic browse view. Returns null when no sidebar
 * item matches (e.g. the `private`/`crowdfunding` footer filters, which the
 * Explore page maps to the sidebar `view` instead).
 */
export const resolveSelectionFromSlug = (slug?: string | null): FilterSelection | null => {
  const target = (slug ?? '').trim();
  if (!target) return null;
  for (const group of SIDE_GROUPS) {
    const item = group.items.find(
      (it) => slugify(it.key) === target || slugify(it.label) === target,
    );
    if (item) return { group: group.label, item };
  }
  return null;
};
